import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  Eye, 
  Star, 
  MessageSquare, 
  Heart, 
  PlusCircle, 
  CheckCircle2, 
  Clock, 
  Layers,
  ArrowRight
} from "lucide-react";
import { APP_NAME } from "@/lib/constants";
import { SEO } from "@/components/SEO";

const steps = [
  {
    number: "01",
    title: "הגשת המערכת",
    description: "מתחילים במילוי טופס קצר ומדויק. תצטרכו להזין את שם המערכת, קטגוריה מתאימה, ותיאור (קצר ומפורט). אל תשכחו להוסיף קישור לאתר שלכם, תמונת כיסוי מושכת וגלריית צילומי מסך שמציגה את היכולות של המערכת."
  },
  {
    number: "02",
    title: "בדיקה ואישור",
    description: `צוות ${APP_NAME} בודק כל מערכת באופן ידני כדי לוודא תקינות קישורים, איכות תוכן ועמידה בסטנדרטים של הקהילה. המערכת תופיע בסטטוס "ממתין" ותעבור ל"מאושר" תוך 48 שעות. תקבלו התראה מיידית על כל שינוי בסטטוס.`
  },
  {
    number: "03",
    title: "המערכת באוויר",
    description: "ברגע האישור, המערכת שלכם מופיעה בקטלוג הראשי וחשופה לאלפי משתמשים. מאותו רגע, נפתח עבורכם לוח בקרה אישי שבו תוכלו לעקוב אחרי הביצועים, לענות לשאלות גולשים ולנהל את הנוכחות שלכם בפלטפורמה."
  }
];

const metrics = [
  {
    icon: Eye,
    value: "1,240",
    label: "צפיות בעמוד",
    description: "מעקב מדויק אחרי כמות המשתמשים שנחשפו למערכת שלכם, כולל מגמות צמיחה יומיות ושבועיות."
  },
  {
    icon: Star,
    value: "4.8",
    label: "דירוג ממוצע",
    description: "הציון המצטבר שלכם המבוסס על ביקורות גולשים מאומתות. דירוג גבוה בונה אמון ומגדיל חשיפה."
  },
  {
    icon: MessageSquare,
    value: "12",
    label: "שאלות מהקהילה",
    description: "מרכז תקשורת ישיר שבו גולשים שואלים על המערכת. מענה מהיר עוזר להפוך מתעניינים למשתמשים."
  },
  {
    icon: Heart,
    value: "86",
    label: "שמירות למועדפים",
    description: "מדד המראה כמה משתמשים סימנו את המערכת שלכם כדי לחזור אליה שוב בעתיד."
  }
];

const PublisherGuide = () => {
  return (
    <Layout>
      <SEO 
        title="מדריך למפרסם" 
        description={`המדריך המלא לפרסום מערכת בקטלוג של ${APP_NAME}. הדרך שלכם להגיע לקהל המשתמשים הנכון מתחילה כאן.`} 
      />
      <div className="bg-white min-h-screen pt-32 pb-20" dir="rtl">
        <div className="container mx-auto px-6 max-w-4xl">
          
          {/* Hero Section */}
          <header className="mb-24 text-right">
            <p className="text-xs font-bold tracking-widest text-slate-400 uppercase mb-3">מדריך מקצועי</p>
            <h1 className="text-4xl lg:text-5xl font-black text-slate-900 tracking-tight mb-4 leading-tight">
              פרסום מערכת ב-{APP_NAME}
            </h1>
            <p className="text-lg text-slate-500 leading-relaxed max-w-2xl font-light">
              הדרך שלכם להגיע לקהל המשתמשים הנכון מתחילה כאן. מהגשת הטופס ועד למעקב נתונים מתקדם בזמן אמת.
            </p>
          </header>

          {/* Process Steps */}
          <section className="space-y-12 mb-32">
            <div className="space-y-8">
              {steps.map((step, idx) => (
                <div key={idx} className="group relative flex gap-8 items-start p-8 rounded-2xl border border-slate-100 transition-colors">
                  <div className="text-3xl font-black text-slate-200 group-hover:text-blue-600/20 transition-colors shrink-0 pt-1">
                    {step.number}
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-xl font-bold text-slate-900">{step.title}</h3>
                    <p className="text-slate-600 leading-relaxed text-base">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Analytics Section */}
          <section className="mb-32">
            <div className="text-right mb-12">
              <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-3">הנתונים שלכם, במקום אחד</h2>
              <p className="text-slate-500">לוח הבקרה מציג נתוני ביצועים בזמן אמת על כל מערכת שפרסמתם</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {metrics.map((metric, idx) => (
                <div key={idx} className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 flex flex-col items-center text-center group hover:bg-white hover:shadow-sm transition-all">
                  <div className="p-2.5 bg-white rounded-xl text-slate-400 group-hover:text-blue-600 transition-colors mb-4 border border-slate-100">
                    <metric.icon className="h-5 w-5" />
                  </div>
                  <div className="text-2xl font-black text-slate-900 mb-1">{metric.value}</div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">{metric.label}</div>
                  <p className="text-[11px] leading-relaxed text-slate-500 font-medium">
                    {metric.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Bottom CTA */}
          <section className="pt-16 border-t border-slate-100 text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">מוכנים להתחיל?</h2>
            <p className="text-slate-500 mb-10 text-lg">הצטרפו למאות יוצרים שכבר פרסמו את המערכות שלהם.</p>
            
            <div className="flex flex-col items-center gap-4">
              <Link to="/dashboard/create">
                <Button className="h-14 px-10 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-lg gap-2 shadow-lg shadow-slate-100">
                  <PlusCircle className="h-5 w-5" />
                  הוספת מערכת חדשה
                </Button>
              </Link>
              <div className="text-xs font-bold text-slate-400 flex items-center gap-2">
                <span>ללא עלות</span>
                <span className="w-1 h-1 rounded-full bg-slate-200" />
                <span>אישור תוך 48 שעות</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
};

export default PublisherGuide;